import {
  FieldLabel,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { AccountAccessOptionsFields } from 'src/components/AccountAccessOptions/AccountAccessOptions'
import Box from 'src/components/Box/Box'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ErrorList from 'src/components/ErrorList/ErrorList'
import UserAndNameInputBox from 'src/components/UserAndNameInputBox/UserAndNameInputBox'
import {
  UserDropdownInfiniteScrollProps,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import { OrganizationRoles } from 'src/enums'
import { FormFieldsValidation, useFormFields } from 'src/hooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { PostUserInvitesRequest } from 'src/requests/postUserInvites.request'
import { stringArrayIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type EnterEmailsFormValues = {
  userEmails?: string[]
}

interface EnterEmailsFormProps {
  className?: string
  'data-testid'?: string
  postingUserInvites: boolean
  topLevelError?: string
  postUserInvites: (body: PostUserInvitesRequest) => void
  buttonText: React.ReactElement
  inputPlaceHolderText?: React.ReactElement
  onClick?: (role?: OrganizationRoles) => void
  showCounter?: boolean
  maxInputNumber?: number
  prePopulatedEmails?: string[]
  dropdownProps?: {
    infiniteScrollProps: UserDropdownInfiniteScrollProps
    userEmailOptions: UserOption[]
  }
  showRoles?: boolean
  setRole?: (role: OrganizationRoles) => void
  setBookkeeperAccess?: (fields: AccountAccessOptionsFields) => void
  dontShowMaxInputError?: boolean
}

const EnterEmailsForm: React.FC<EnterEmailsFormProps> = (
  props,
): React.ReactElement => {
  const locale = useSelector(selectNullSafeLocaleFromState)

  const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null)

  const [selectedIndex, setSelectedIndex] = useState<number | undefined>()
  const [emails, setEmails] = useState<string[]>(props.prePopulatedEmails || [])
  const [inputEmail, setInputEmail] = useState<string>('')

  const onInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const val = e.target.value
    if (val.includes(',')) {
      const newEmails = [
        ...emails,
        ...val.split(',').filter((email) => !!email.replace(/\s/g, '')),
      ]
      setEmails(newEmails)
      setInputEmail('')
    } else {
      setInputEmail(val.replace(/\s/g, ''))
    }
  }

  const {
    fields: flatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
  } = useFormFields<EnterEmailsFormValues>(
    {
      userEmails: props.prePopulatedEmails,
    },
    (): FormFieldsValidation<EnterEmailsFormValues> => {
      return {
        userEmails: [stringArrayIsEmpty],
      }
    },
  )

  useEffect((): (() => void) => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Backspace') {
        if (selectedIndex !== undefined) {
          const newEmails = emails.filter((val, i) => i !== selectedIndex)
          setEmails(newEmails)
          setSelectedIndex(undefined)
        } else if (emails.length > 0 && !inputEmail) {
          const newEmails = emails.slice(0, emails.length - 1)
          setEmails(newEmails)
          setSelectedIndex(undefined)
        }
        if (inputRef.current) {
          inputRef.current.focus()
        }
      }
      if ((e.key === 'Enter' || e.key === ' ') && inputEmail) {
        const newEmails = [...emails, inputEmail]
        setEmails(newEmails)
        setInputEmail('')
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return (): void => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [selectedIndex, inputEmail, emails])

  const onBlur = (): void => {
    if (inputEmail) {
      const newEmails = [...emails, inputEmail]
      setEmails(newEmails)
      setInputEmail('')
    }
  }

  const onButtonClick = (): void => {
    if (props.onClick) {
      props.onClick()
    }
    if (props.dropdownProps) {
      props.postUserInvites({
        emails: flatFields.userEmails || [],
      })
    } else {
      props.postUserInvites({
        emails: emails,
      })
    }
  }

  const onEmailClick = (i: number): void => {
    if (selectedIndex !== i) {
      setSelectedIndex(i)
    } else {
      setSelectedIndex(undefined)
    }
  }

  const bottomElement = (
    <div
      css={css`
        display: flex;
        flex-direction: row;
        width: 100%;
      `}
    >
      <div
        css={css`
          flex-grow: 1;
        `}
      >
        {props.topLevelError !== undefined && (
          <ErrorList errors={[props.topLevelError]} />
        )}
        {props.maxInputNumber !== undefined &&
          !props.dontShowMaxInputError &&
          flatFields.userEmails !== undefined &&
          flatFields.userEmails.length > props.maxInputNumber && (
            <ErrorList
              errors={[
                <Trns key={'Too many emails'} source={'Too many emails'} />,
              ]}
            />
          )}
      </div>
      {props.showCounter && (
        <div
          css={css`
            margin-top: 5px;
            ${getFontCss(
              12,
              styleConstants.colors.gray_dark,
              styleConstants.fontFamilies.regular,
            )}
          `}
        >
          {`${flatFields?.userEmails?.length || 0}/${props.maxInputNumber}`}
        </div>
      )}
    </div>
  )

  return (
    <div>
      {!props.dropdownProps ? (
        <Box
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          <FieldLabel>
            <Trns source={'Email Addresses'} />
          </FieldLabel>
          <div
            css={css`
              border-radius: 6px;
              background-color: ${styleConstants.colors.gray_lightest};
              display: flex;
              flex-wrap: wrap;
              padding: 16px;
              border: 1px solid ${styleConstants.colors.gray_medium};

              ${props.topLevelError !== undefined &&
              css`
                border: 1px solid ${styleConstants.colors.red};
                &:focus,
                &:hover {
                  border: 1px solid ${styleConstants.colors.red};
                }
              `}
            `}
          >
            {(emails || []).map((email, i) => (
              <div
                key={i}
                css={css`
                  padding: 2px 6px;
                  ${getFontCss(
                    14,
                    themeConstants.colors.cta,
                    styleConstants.fontFamilies.medium,
                  )};
                  cursor: pointer;
                  border-radius: 4px;
                  margin-right: 4px;

                  ${selectedIndex === i &&
                  css`
                    background-color: ${themeConstants.colors.cta};
                    ${getFontCss(
                      14,
                      styleConstants.colors.white,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                `}
                onClick={(): void => onEmailClick(i)}
                onKeyPress={(): void => onEmailClick(i)}
                role="button"
                tabIndex={0}
              >
                {`${email},`}
              </div>
            ))}
            <input
              css={css`
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
                background-color: inherit;
                border: none;
                flex-grow: 2;
                caret-color: ${themeConstants.colors.cta};
                padding: 2px 6px;

                &::placeholder {
                  opacity: 0.5;
                  ${getFontCss(
                    14,
                    styleConstants.colors.gray_darkest,
                    styleConstants.fontFamilies.regular,
                  )};
                }
              `}
              ref={inputRef}
              value={inputEmail}
              onChange={onInputChange}
              placeholder={
                emails.length > 0
                  ? translateSingleString(locale, 'Enter more emails...')
                  : translateSingleString(locale, 'Enter one or more emails...')
              }
              onBlur={onBlur}
            />
          </div>
          {bottomElement}
        </Box>
      ) : (
        <UserAndNameInputBox
          data-testid={props['data-testid']}
          displayNameFieldLabel={<Trns source={'Email Addresses'} />}
          userFieldLabel={<Trns source={'Email Addresses'} />}
          userEmailProps={{
            value: flatFields.userEmails,
            errors: flatFieldsErrors.userEmails,
            onChange: (val: string[]): void => {
              onFlatFieldChange('userEmails', val)
            },
            isStatic: false,
          }}
          userOptions={props.dropdownProps.userEmailOptions}
          userInfiniteScrollProps={props.dropdownProps.infiniteScrollProps}
          isMulti
          displayPills
          bottomElement={bottomElement}
          showRoles={props.showRoles}
          setRole={props.setRole}
          setBookkeeperAccess={props.setBookkeeperAccess}
          errorBorder={!!props.topLevelError}
        />
      )}
      <Button
        css={css`
          margin-top: 15px;
        `}
        onClick={onButtonClick}
        design={ButtonDesigns.designCta}
        type={ButtonTypes.button}
        text={props.buttonText}
        loading={props.postingUserInvites}
        big
        data-testid="invite-confirm-btn"
      />
    </div>
  )
}

export default EnterEmailsForm
